import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { BiCheck } from 'react-icons/bi';

import AsyncButton from 'modules/common/components/AsyncButton';
import { isEmpty } from 'lodash';
import { aclHelper } from 'helper';

import {
  useAllPermissions,
  usePermissionsById,
  useUpdatePermission,
} from '../hooks';

const permissionsTitle = ['read', 'write', 'update', 'delete', 'execute'];
const permissionsArr = [
  { id: 0, permission: 'r' },
  { id: 1, permission: 'w' },
  { id: 2, permission: 'u' },
  { id: 3, permission: 'd' },
  { id: 4, permission: 'x' },
];

const Permissions = ({ roleId }) => {
  const [isAllPermissionsLoading, allPermissions] = useAllPermissions();
  const [isPermissionsByIdLoading, permissionsById] =
    usePermissionsById(roleId);
  const [isAdd, setAdd] = useState(false);
  const [pending, setPending] = useState(false);

  const isLoading = useMemo(
    () => isAllPermissionsLoading || isPermissionsByIdLoading,
    [isPermissionsByIdLoading, isAllPermissionsLoading],
  );

  const mapPermissions = useMemo(
    () =>
      allPermissions?.map((item) => ({
        ...item,
        access:
          permissionsById?.find((x) => x?.name === item?.name)?.access || [],
      })),
    [allPermissions, permissionsById],
  );

  const [newPermissions, setNewPermissions] = useState(mapPermissions);

  const getPermissions = useMemo(
    () => (isAdd ? newPermissions : mapPermissions),
    [mapPermissions, newPermissions, isAdd],
  );

  const onError = () => setPending(false);
  const onSuccess = () => {
    setAdd(false);
    setPending(false);
  };

  const [isUpdateLoading, update] = useUpdatePermission(onSuccess, onError);

  const handleCheckbox = (item, permission, index, permissionIndex) => {
    const newArr = [...newPermissions];
    const newAccess = [...newArr[index]?.access];
    const isIncludes = newAccess?.includes(permission);

    if (isIncludes) {
      const filterAccess = newAccess?.filter((p) => p !== permission);

      newArr[index] = {
        ...newArr[index],
        access: filterAccess,
      };

      setNewPermissions(newArr);
    } else {
      // eslint-disable-next-line no-extend-native
      Array.prototype.insert = function (i, x) {
        // eslint-disable-next-line react/no-this-in-sfc
        this.splice(i, 0, x);
      };

      newAccess.insert(permissionIndex, permission);

      newArr[index] = {
        ...newArr[index],
        access: newAccess,
      };

      setNewPermissions(newArr);
    }
  };

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      update({
        permissions: newPermissions
          ?.map((item) => ({
            id: item?.id,
            access: item?.access,
          }))
          .filter((item) => !isEmpty(item?.access)),
        id: roleId,
      });
      console.log(
        newPermissions
          ?.map((item) => ({
            id: item?.id,
            access: item?.access,
          }))
          .filter((item) => !isEmpty(item?.access)),
      );
    },
    [newPermissions, roleId, update],
  );

  useEffect(() => {
    if (!mapPermissions.length) return;
    setNewPermissions(mapPermissions);
    setAdd(false);
  }, [mapPermissions, roleId]);

  const renderPermission = (item, permission, index, permissionIndex) => (
    <div>
      {isAdd ? (
        <div>
          {item?.available_access?.includes(permission) && (
            <input
              type="checkbox"
              className="text-primary tracking-wide cursor-pointer rounded-sm"
              onChange={() =>
                handleCheckbox(item, permission, index, permissionIndex)
              }
              checked={item?.access?.includes(permission)}
            />
          )}
        </div>
      ) : (
        <div>
          {item?.access?.includes(permission) && (
            <BiCheck className="text-primary text-lg" />
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="py-6">
      {isLoading || !mapPermissions.length ? (
        <div>
          {isLoading ? (
            <div className="grid grid-cols-2 gap-3 p-3">
              <div className="animate-pulse bg-gray-50 h-8 w-full rounded" />
              <div className="animate-pulse bg-gray-50 h-8 w-full rounded" />
              <div className="col-span-2 animate-pulse bg-gray-50 h-8 w-full rounded" />
              <div className="col-span-2 animate-pulse bg-gray-50 h-8 w-full rounded" />
              <div className="col-span-2 animate-pulse bg-gray-50 h-8 w-full rounded" />
            </div>
          ) : (
            <div className="text-center pt-5">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Empty</h3>
              <p className="mt-1 text-sm text-gray-500">There are no items.</p>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="pl-3 flex justify-between items-center mb-8">
            <h5 className="text-lg font-bold text-gray-700">PERMISSIONS</h5>
            {isAdd ? (
              <form onSubmit={handleSubmit} className="flex gap-3">
                <AsyncButton
                  onClick={() => setAdd(false)}
                  className=" !border-primary hover:!border-blue"
                  color="regular-with-border-gray"
                >
                  cancel
                </AsyncButton>
                <AsyncButton
                  type="submit"
                  isLoading={pending || isUpdateLoading}
                  disabled={pending || isUpdateLoading}
                >
                  save
                </AsyncButton>
              </form>
            ) : (
              aclHelper(
                'role_permission',
                'u',
                <AsyncButton
                  className="!px-14 !py-1"
                  disabled={isLoading}
                  onClick={() => setAdd(true)}
                >
                  update
                </AsyncButton>,
              )
            )}
          </div>
          <ul className="grid grid-cols-12 pr-2 mb-6">
            <div className="col-span-7" />
            {permissionsTitle?.map((item) => (
              <li key={item} className="capitalize font-light text-center ">
                {item}
              </li>
            ))}
          </ul>
          <div className="border-y border-gray-50/60">
            {(getPermissions || [])?.map((item, index) => (
              <ul
                key={item?.id}
                className="grid grid-cols-12  even:bg-light-gray-100/80 p-2"
              >
                <li className="col-span-7 capitalize font-light tracking-wide">
                  {item?.name?.replace(/_/g, ' ')}
                </li>

                {permissionsArr?.map((x, i) => (
                  <div key={x?.id} className="flex justify-center">
                    {renderPermission(item, x?.permission, index, i)}
                  </div>
                ))}
              </ul>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

Permissions.propTypes = {
  roleId: PropTypes.string.isRequired,
};

export default memo(Permissions);
